/* eslint-disable prefer-promise-reject-errors */

'use strict';

/**
 * do not add direct dependencies here, that will load JET; see getStateManagement/getServicesManager
 */

define('vb/private/helpers/runtimeManager',[
  'vb/private/utils',
  'vb/private/constants',
  'vb/private/history',
  'vb/private/stateManagement/stateMonitor',
  'vb/private/stateManagement/navigationContext',
],
(Utils, Constants, History, StateMonitor, NavigationContext) => {
  /**
   * @typedef {import('vb/private/stateManagement/page.js').default} Page
   * @typedef {import('vb/private/stateManagement/flow.js').default} Flow
   * @typedef {import('vb/private/services/definition/pluginServiceDefinition').default} Service
   * @typedef {import('vb/private/translations/bundlesModel').default} BundlesModel
   */

  /**
   * Container type constants
   * @type {{PAGE: string}}
   *
   * TODO: remove this if we remove all xxxActivePagexxx methods
   * TODO: remove all trace of the active page concept and page ready concept.
   *       Users should rely on the containerReady API
   */
  const CONTAINER_TYPE = {
    PAGE: '__VB_PAGE_CONTAINER__',
  };

  // the application path is always ''
  const APPLICATION_PATH = '';

  /**
   * This class is used by the DT to manage the RT to perform actions such as refreshing the current page or
   * navigating to a page.
   *
   * NOTE: This class should not be loaded by the RT.
   */
  class RuntimeManager {
    /**
     * Creates an instance of RuntimeManager.
     *
     * Initializes properties to manage the state and readiness of containers:
     * - `readyFlags` to track the readiness flag of containers.
     * - `readyPromises` to hold promises that resolve when containers are ready.
     * - `readyPromiseResolvers` to store the resolve functions for the readiness promises.
     * - `readyTimeout` to define a timeout period for readiness.
     *
     * Sets up a state change listener to handle state changes and registers it with the StateMonitor.
     *
     * @constructor
     */
    constructor() {
      // make sure all ready flags default to false
      this.readyFlags = {
        [CONTAINER_TYPE.PAGE]: false,
      };

      this.readyPromises = {};
      this.readyPromiseResolvers = {};
      this.readyTimeout = 0;

      this.stateChangeListener = (state, container) => {
        this.handleStateChange(state, container);
      };

      // register a state change listener on the StateMonitor
      StateMonitor.addStateChangeListener(this.stateChangeListener);
    }

    /**
     * Returns a promise that resolves when the application is loaded and ready.
     * If a timeout is set via {@link RuntimeManager#setReadyTimeout}, the promise will be rejected if
     * the application is not ready within the time limit.
     *
     * @returns {Promise<boolean|undefined>} A promise that resolves to `true` when the application is ready,
     * or rejects if the readiness timeout is exceeded.
     */
    applicationReady() {
      return this.containerReady(APPLICATION_PATH);
    }

    /**
     * Returns a promise that resolves when the container of the given path is loaded and ready.
     * If a readiness timeout is set using the {@link RuntimeManager#setReadyTimeout} method,
     * the promise will be rejected if the container does not become ready within the specified time limit.
     *
     * @param {String} path - The full path of the container to check for readiness.
     * @returns {Promise<boolean|undefined>} A promise that resolves to `true` when the container is ready,
     * or rejects if the readiness timeout is exceeded.
     */
    containerReady(path) {
      if (!this.readyPromises[path]) {
        if (this.readyFlags[path]) {
          this.readyPromises[path] = Promise.resolve(true);
        } else {
          this.readyPromises[path] = new Promise((resolve, reject) => {
            // remember resolve so handleStateChange can resolve the promise when the container is ready
            this.readyPromiseResolvers[path] = resolve;

            // reject the promise if the container is not loaded within the timeout
            if (this.readyTimeout > 0) {
              setTimeout(reject, this.readyTimeout);
            }
          });
        }
      }

      return this.readyPromises[path];
    }

    /**
     * Sets the timeout in milliseconds for the `applicationReady` and `containerReady` methods.
     * If the application or page is not ready within the specified time limit, the promise will be rejected.
     *
     * @param {Number} timeout - The timeout duration in milliseconds.
     */
    setReadyTimeout(timeout) {
      this.readyTimeout = timeout;
    }

    /**
     * Refreshes the container identified by the given path. The container must be a page.
     *
     * If the container is a page, it disposes the page, resets the state page path, and refreshes the page.
     * The method then navigates to the refreshed page using its root router. If the container type is not a
     * page, the promise will be rejected.
     *
     * @param {String} path - The full path of a container to be refreshed.
     * @returns {Promise<void>} - A promise that resolves once the container has been refreshed and navigation is
     * complete, or rejects if the container type is unsupported or an error occurs during the refresh process.
     */
    refreshContainer(path) {
      return this.containerReady(path).then(() => this.getStateManagement()).then(([Router, Application, Page]) => {
        const container = Router.getContainer(Application, path);

        if (container instanceof Page) {
          const page = container;

          // dispose the page
          page.dispose();

          // Reset the state pagePath so that refresh does not unnecessarily sync the page
          // pagePath will be set again once the page is refreshed.
          History.setPagePath('');

          // refresh the page
          return this.refreshPage(page)
          // It is very important to use the new page created by refreshPage because
          // page (the old one) is disposed.
            .then((newPage) => {
              const navContext = new NavigationContext(newPage);
              return Application.getLeafPageInstance(page.getNavPath(), navContext);
            })
            .then((leafPage) => leafPage.rootRouter.go(leafPage.getNavPath()));
        }

        return Promise.reject(`Unknown container for ${path}`);
      });
    }

    /**
     * Refreshes the descriptor of the container identified by the given path. The container must be a page.
     *
     * This method clears the cached container descriptor, disposes of the container scope,
     * and refreshes the container. If the container is a page, it also clears the cached
     * page descriptor, disposes of fragments and layouts, and clears extension cache. If the container type is not a
     * page, the promise will be rejected.
     *
     * @param {String} path - The full path of a container to refresh its descriptor.
     * @returns {Promise<Page>} - A promise that resolves once the container's descriptor has been refreshed,
     * or rejects if the container type is unsupported or an error occurs during the refresh process.
     */
    refreshContainerDescriptor(path) {
      return this.containerReady(path).then(() => this.getStateManagement()).then(([Router, Application, Page]) => {
        const container = Router.getContainer(Application, path);

        // clear rules promises and the rules engine too. It appears that parts of the container are still reused
        // when this method is called.
        container.loadRulesPromise = null;
        let re = container.getRulesEngine(false);
        if (re) {
          re.dispose(container);
        }

        if (container instanceof Page) {
          const page = container;

          // clear the cached page descriptor and dispose the page scope
          page.loadPagePromise = null;
          page.loadMetadataPromise = null;
          page.loadBundlesPromise = null;
          page.loadDescriptorPromise = null;
          page.loadAndStartPromise = null;

          // allow the scope to be recreated
          page.initializePromise = null;
          page.viewModelPromise = null;

          // Clear extension cache
          page.extensionsArray = [];
          page.extensions = {};

          // dispose fragments as page descriptor is being cleared without the page itself being disposed
          Object.keys(page.fragments).forEach((fragId) => {
            const frag = page.fragments[fragId];
            if (frag) {
              frag.disposeScope();
            }
          });
          // Dispose of layout scopes in the page
          page.layouts.forEach((layout) => layout.disposeScope());
          page.disposeScope();

          // Clear the availableContexts so that anything derived from expressionContext is derived again.
          // e.g. $translations is only constructed on the first access to the property in availableContexts.
          page.availableContexts = null;

          // refresh the page
          return this.refreshPage(page);
        }

        return Promise.reject(`Unsupported container for ${path}`);
      });
    }

    /**
     * Refreshes the custom functions of the container identified by the given path. The container must be a page.
     *
     * This method clears the cached functions promise for the page and refreshes the page,
     * allowing the functions to be reloaded and reinitialized. If the container type is not a page,
     * the promise will be rejected.
     *
     * @param {String} path - The full path of a container to refresh its functions.
     * @returns {Promise<Page>} - A promise that resolves once the container's functions have been refreshed,
     * or rejects if the container type is unsupported or an error occurs during the refresh process.
     */
    refreshContainerFunctions(path) {
      return this.containerReady(path).then(() => this.getStateManagement()).then(([Router, Application, Page]) => {
        const container = Router.getContainer(Application, path);

        if (container instanceof Page) {
          const page = container;

          // clear the cached functions
          page.loadFunctionsPromise = null;

          // allow the scope to be recreated
          page.initializePromise = null;
          page.viewModelPromise = null;
          page.disposeScope();

          // refresh the page
          return this.refreshPage(page);
        }

        return Promise.reject(`Unsupported container for ${path}`);
      });
    }

    /**
     * Refreshes the page template of the container identified by the given path. The container must be a page.
     *
     * This method clears the cached template-related promises for the page and refreshes the page,
     * allowing the template to be reloaded and reinitialized. If the container type is not a page,
     * the promise will be rejected.
     *
     * @param {String} path - The full path of a container to refresh its template.
     * @returns {Promise<Page>} - A promise that resolves once the container's template has been refreshed,
     * or rejects if the container type is unsupported or an error occurs during the refresh process.
     */
    refreshContainerTemplate(path) {
      return this.containerReady(path).then(() => this.getStateManagement()).then(([Router, Application, Page]) => {
        const container = Router.getContainer(Application, path);

        if (container instanceof Page) {
          const page = container;

          // clear the cached page template
          page.loadPagePromise = null;
          page.loadHtmlPromise = null;
          page.viewModelPromise = null;

          // refresh the page
          return this.refreshPage(page);
        }

        return Promise.reject(`Unsupported container for ${path}`);
      });
    }

    /**
     * Reloads the bundles for the given container and rebuilds the expression binding context.
     *
     * This method clears the cached translation bundles and, for page containers,
     * clears the initialization and view model promises to allow the scope to be
     * recreated. If the container type is supported, it reloads the translation bundles.
     *
     * @param {String} path - The full path of a container.
     * @returns {Promise<BundlesModel>} - A promise that resolves once the container's translation bundles have been
     * reloaded, or rejects if the container type is unsupported or an error occurs during the reload process.
     * @throws {Error} - Throws an error if the container type is unsupported.
     */
    refreshContainerBundles(path) {
      let container;
      return this.containerReady(path).then(() => this.getStateManagement()).then(([Router, Application, Page]) => {
        container = Router.getContainer(Application, path);

        if (container) {
          container.loadBundlesPromise = null;
          if (container instanceof Page) {
            // allow the scope to be recreated
            container.initializePromise = null;
            container.viewModelPromise = null;
          }
          // do NOT dispose of scope here - we are just reloading translations!  container.disposeScope();
          return container.loadTranslationBundles();
        }
        throw new Error(`Unsupported container for ${path}`);
      });
    }

    /**
     * Navigates to the page specified by the given page path.
     * The page path must be absolute.
     * For example, to navigate to another top-level page, the path should be `/pageId`.
     * To navigate to a nested page: `/pageId/flowId/pageId`.
     * The leading slash is optional.
     *
     * Returns a promise that resolves as soon as the navigation is executed.
     * Note: this is not when the page is ready. To know when the page is ready,
     * `navigateToPage` should be followed by `containerReady(pagePath)`.
     *
     * @param {String} pagePath - The path of the page to navigate to.
     * @param {Array<{key: String, value: String}>} parameters - A map of page input parameters.
     * @returns {Promise<Boolean>} - A Promise that resolves to `true` if the navigation was successful.
     */
    navigateToPage(pagePath, parameters) {
      // TODO If would make sense to return the containerReady promise.
      return this.applicationReady().then(() => this.getStateManagement())
        // Always navigate relative from the root (app flow)
        // The promise returned resolves with no arguments
        .then(([Router, Application]) => Router.queueNavigate(Application, {
          page: pagePath,
          params: parameters,
          operation: Constants.NavigateOperation.PAGE_OLD,
        }).then((result) => (result && result.navigated === true)));
    }

    // private methods

    /**
     * Refreshes the given page instance.
     *
     * This method performs a complete refresh of the page instance, resetting its readiness state,
     * mutating its parent `ojModule` to reload the view, and allowing the page to be restarted.
     *
     * The lifecycle state of the page is set to `REFRESHING` to prevent the page from being disposed
     * by `ojModule` during the refresh process. Any necessary navigation context is also provided
     * to load and enter the new page instance. If the same page instance is being reused, its lifecycle
     * state is re-set to `REFRESHING` to ensure the proper state is maintained.
     *
     * @param {Page} page - The page instance to reload.
     * @returns {Promise<Page>} - A promise resolving to the new or refreshed page instance.
     * @private
     */
    refreshPage(page) {
      const path = page.getNavPath();
      const flow = page.parent;

      // reset the page ready state
      this.setContainerReady(path, false);

      // Mutates parent ojModule in order to reload the view
      page.resetParentModuleConfig();

      // set the lifecycleState to refreshing to prevent the page from getting disposed by ojModule
      page.lifecycleState = Constants.ContainerState.REFRESHING; // eslint-disable-line no-param-reassign

      // allow the page to be restarted
      page.started = false; // eslint-disable-line no-param-reassign

      const navContext = new NavigationContext(flow);

      return flow.loadPageFromInfo({ id: page.id }, navContext).then((newPage) => {
        newPage.enter();

        // if we're reusing the same page instance, we need to reset the lifecycleState back to refreshing to
        // prevent the page from getting disposed by ojModule since enter sets the state to entered
        if (newPage === page) {
          newPage.lifecycleState = Constants.ContainerState.REFRESHING; // eslint-disable-line no-param-reassign
        }

        return newPage;
      });
    }

    /**
     * Refreshes the service identified by the given ID.
     *
     * This method first disposes of the existing service using the service manager, and then reloads
     * the service to ensure that it is refreshed.
     *
     * @param {String} id - The ID of the service to be refreshed.
     * @returns {Promise<Service>} - A Promise that resolves with the refreshed service.
     */
    refreshService(id) {
      return this.getServicesManager()
        .then((ServicesManager) => ServicesManager.disposeService(id))
        .then((services) => services.load([id]));
    }

    /**
     * Disposes the CatalogHandler without reloading it immediately.
     *
     * This method disposes the CatalogHandler from the protocolRegistry. The CatalogHandler
     * will not be reloaded until the service layer requires it.
     *
     * @returns {Promise<void>} - A promise that resolves once the CatalogHandler has been disposed.
     */
    refreshServiceExtensionCatalog() {
      return this.getStateManagement()
        .then(([, Application]) => Application.protocolRegistry && Application.protocolRegistry.disposeCatalog());
    }

    /**
     * Forces a refresh of the extension manifest after a DT (Design Time) change.
     *
     * This method retrieves the current `ConfigLoader` singleton instance and
     * refreshes the extension registry to reflect the latest changes made in
     * the design time environment.
     *
     * @returns {Promise<void>} - A promise that resolves when the refresh is complete.
     */
    // eslint-disable-next-line class-methods-use-this
    refreshExtensionManifest() {
      // configLoader is a singleton so this will always return the current extensionRegistry class
      return Utils.getResource('vb/private/configLoader')
        .then((ConfigLoader) => ConfigLoader.extensionRegistry.refreshFromDT());
    }

    /**
     * Refreshes the flow identified by the given flow instance.
     *
     * This method is intended to refresh the state or content of a flow container.
     * The `flow` parameter is expected to be an instance of the flow to be refreshed.
     * Details of what exactly constitutes a flow refresh, or what actions need
     * to be performed, should be implemented within this method.
     *
     * Note: The current implementation does not perform any actions. This is a placeholder
     * and should be implemented based on the specific requirements.
     *
     * @param {Flow} flow - The Flow instance to be refreshed.
     */
    refreshFlow(flow) { // eslint-disable-line no-unused-vars,class-methods-use-this
    }

    /**
     * Disposes of the instance of the `RuntimeManager` class.
     *
     * This method performs cleanup tasks such as removing the state change listener from the `StateMonitor` to ensure
     * that no further state changes are processed by this instance.
     */
    dispose() {
      // remove the stateChangeListener from StateMonitor
      StateMonitor.removeStateChangeListener(this.stateChangeListener);
    }

    /**
     * Handles state changes for containers and updates their readiness status.
     *
     * This method is registered as a state change listener and is triggered whenever the state of a container changes.
     * If the state of the container is `CONTAINER_ACTIVATED`, it sets the container's readiness flag to `true`.
     *
     * @param {String} state - The current state of the container, which can be one of the following:
     *   - `PAGE_ACTIVATED`
     *   - `PAGE_DEACTIVATED`
     *   - `FRAGMENT_ACTIVATED`
     *   - `FRAGMENT_DEACTIVATED`
     *   - `CONTAINER_ACTIVATED`
     *   - `CONTAINER_DEACTIVATED`
     * @param {Object} container - The container whose state has changed.
     * @param {Function} container.getNavPath - Method to get the navigation path of the container.
     */
    handleStateChange(state, container) {
      if (state === StateMonitor.RuntimeState.CONTAINER_ACTIVATED) {
        this.setContainerReady(container.getNavPath(), true);
      }
    }

    /**
     * Toggles the application ready flag.
     *
     * This method sets the readiness state of the entire application. It utilizes
     * the `setContainerReady` method to update the readiness state of the application
     * container identified by the constant `APPLICATION_PATH`.
     *
     * @param {boolean} flag - The flag indicating the readiness state to set for the application.
     *                        - `true` to mark the application as ready.
     *                        - `false` to mark the application as not ready.
     * @private
     */
    setApplicationReady(flag) {
      this.setContainerReady(APPLICATION_PATH, flag);
    }

    /**
     * Toggles the page ready flag.
     *
     * This method updates the readiness state of a page container. It uses the `setContainerReady`
     * method to set the readiness flag for containers identified by `CONTAINER_TYPE.PAGE`.
     *
     * @param {boolean} flag - The flag indicating the readiness state to set for the page container.
     *                        - `true` to mark the page as ready.
     *                        - `false` to mark the page as not ready.
     * @private
     */
    setPageReady(flag) {
      this.setContainerReady(CONTAINER_TYPE.PAGE, flag);
    }

    /**
     * Toggles the container ready flag for the given path.
     *
     * This method updates the readiness state of a container identified by the given path.
     * It sets the readiness flag and resolves or nullifies the readiness promise based on
     * the provided flag value.
     *
     * @param {String} path - The full path of a container.
     * @param {Boolean} flag - Flag to set indicating the readiness state of the container.
     *                         - `true` to mark the container as ready.
     *                         - `false` to mark the container as not ready.
     * @private
     */
    setContainerReady(path, flag) {
      // return if there's no change
      if (this.readyFlags[path] === flag) {
        return;
      }

      this.readyFlags[path] = flag;

      if (flag) {
        // resolve the readyPromise
        if (this.readyPromiseResolvers[path]) {
          this.readyPromiseResolvers[path]();
          this.readyPromiseResolvers[path] = null;
        }
      } else {
        this.readyPromiseResolvers[path] = null;
        this.readyPromises[path] = null;
      }
    }

    /**
     * Utility function to ensure that any dependencies on JET are loaded only when necessary.
     * This approach defers loading the router until it is needed, preventing JET from being pulled in prematurely.
     * It is also important to configure any bundle overrides for built-in JET translations BEFORE JET is loaded.
     *
     * This method returns a promise that resolves once the necessary state management modules are loaded.
     *
     * @returns {Promise<Array>} - A promise that resolves with an array containing the Router, Application, and
     * Page modules.
     */
    getStateManagement() {
      this.stateManagementPromise = this.stateManagementPromise || Utils.getResources([
        'vb/private/stateManagement/router',
        'vb/private/stateManagement/application',
        'vb/private/stateManagement/page',
      ]);

      return this.stateManagementPromise;
    }

    /**
     * Utility function to ensure that the services manager, and any dependencies on JET,
     * are loaded only when necessary.
     *
     * This approach defers loading the services manager until it is needed, preventing JET
     * from being pulled in prematurely. It is essential to configure any bundle overrides
     * for built-in JET translations BEFORE JET is loaded.
     *
     * @returns {Promise<Object>} - A promise that resolves with the services manager module.
     */
    getServicesManager() {
      this.serviceManagerPromise = this.serviceManagerPromise
        || Utils.getResource('vb/private/services/servicesManager');

      return this.serviceManagerPromise;
    }
  }

  return new RuntimeManager();
});

